import type { MouseEvent, RefObject } from 'react'
import { Plus, Settings } from 'lucide-react'
import { cn } from '@teach/ui'
import type { Block } from '@/types/blocks'
import { getTypeChip } from '@/features/editor/constants/blockTypeColors'
import { typeIcon, typeMeta } from './blockTypeLabels'
import { useMessages } from '@/i18n'
import { messages as blockMessages } from '../blocks/messages'
import { messages } from './messages'
import { useAppDispatch } from '@/store/hooks'
import { setBlockSheetOpen } from '@/features/editor/editorSlice'

interface Props {
  block: Block
  index: number
  selected: boolean
  multiSelected: boolean
  /** Блок занят прогоном — точка-предупреждение вместо плашки миниатюры. */
  inRun: boolean
  onSelect: () => void
  onToggleSelect: () => void
  innerRef?: RefObject<HTMLDivElement>
}

/**
 * Экран в свёрнутой ленте: «номер + значок типа» в цвете типа, как язычок
 * миниатюры. Клик выбирает, Ctrl/Cmd-клик добавляет в мультивыбор. Удалить и
 * копировать — из панели блока или в развёрнутой ленте.
 */
export const CompactBlockChip = ({
  block,
  index,
  selected,
  multiSelected,
  inRun,
  onSelect,
  onToggleSelect,
  innerRef,
}: Props) => {
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  const TypeIcon = typeIcon(block)
  const { label, chipId } = typeMeta(block)
  const handleClick = (e: MouseEvent<HTMLDivElement>) =>
    e.ctrlKey || e.metaKey ? onToggleSelect() : onSelect()
  return (
    <div
      ref={innerRef}
      role="button"
      tabIndex={0}
      data-testid="block-chip"
      aria-label={`${index + 1}. ${label}`}
      aria-pressed={selected}
      title={label}
      onClick={handleClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? onSelect() : undefined)}
      className={cn(
        'relative flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-full px-2.5 text-xs font-bold transition hover:-translate-y-0.5',
        getTypeChip(chipId),
        selected ? 'ring-2 ring-brand ring-offset-2 ring-offset-background' : null,
        multiSelected ? 'outline outline-2 outline-success' : null,
        block.id < 0 ? 'pointer-events-none opacity-70' : null,
      )}
    >
      {index + 1}
      <TypeIcon className="h-3.5 w-3.5" />
      {/* Телефон/планшет: у выбранного экрана — шестерёнка настроек, как у урока на
          плашке; открывает шторку с настройками этого экрана. */}
      {selected ? (
        <button
          type="button"
          aria-label={m.openBlockPanel}
          title={m.openBlockPanel}
          onClick={(e) => {
            e.stopPropagation()
            dispatch(setBlockSheetOpen(true))
          }}
          className="-mr-1 ml-0.5 flex size-5 items-center justify-center rounded-full opacity-60 transition-opacity hover:opacity-100 lg:hidden"
        >
          <Settings className="size-3.5" />
        </button>
      ) : null}
      {inRun ? (
        <span aria-hidden className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-warning" />
      ) : null}
    </div>
  )
}

/** «+» свёрнутой ленты — того же размера, что плашки экранов. */
export const CompactAddButton = ({
  onClick,
  disabled,
  pressed,
  title,
}: {
  onClick: () => void
  disabled: boolean
  pressed: boolean
  title?: string
}) => {
  const m = useMessages(blockMessages)
  return (
    <button
      type="button"
      aria-label={m.addBlock}
      aria-pressed={pressed}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-50',
        pressed
          ? 'border-2 border-primary bg-primary/15 text-primary'
          : 'border-dashed border-muted-foreground/40 text-muted-foreground hover:border-primary/50 hover:text-primary',
      )}
    >
      <Plus className="h-4 w-4" />
    </button>
  )
}
